import type { Project } from '@/domain/types';
import { runCommand } from './registry';
import type { CommandRecord } from './types';

/**
 * Undo history.
 *
 * Because commands are pure and build their results with structural sharing, a
 * history entry is just a reference to the project as it was. Undo is a pointer
 * move, not a replay, so it is O(1) and cannot drift from the forward path — a
 * class of bug that inverse-command undo systems are prone to.
 *
 * Consecutive commands that share a `coalesceKey` inside `coalesceWindowMs`
 * collapse into one entry, so dragging a fader for two seconds is one "Undo",
 * not two hundred.
 */

export interface HistoryEntry {
  /** Project state *before* the command ran. */
  project: Project;
  label: string;
  coalesceKey: string | null;
  at: number;
}

export interface ExecuteResult {
  project: Project;
  changed: boolean;
  label: string;
}

export interface CommandStackOptions {
  limit?: number;
  coalesceWindowMs?: number;
  now?: () => number;
}

export class CommandStack {
  private undoStack: HistoryEntry[] = [];
  private redoStack: HistoryEntry[] = [];
  private readonly limit: number;
  private readonly coalesceWindowMs: number;
  private readonly now: () => number;

  constructor(options: CommandStackOptions = {}) {
    this.limit = options.limit ?? 200;
    this.coalesceWindowMs = options.coalesceWindowMs ?? 900;
    this.now = options.now ?? (() => Date.now());
  }

  execute(project: Project, record: CommandRecord): ExecuteResult {
    const result = runCommand(project, record);
    if (!result.changed) return { project, changed: false, label: result.label };

    const previous = this.undoStack[this.undoStack.length - 1];
    const canCoalesce =
      previous !== undefined &&
      result.coalesceKey !== null &&
      previous.coalesceKey === result.coalesceKey &&
      this.now() - previous.at <= this.coalesceWindowMs;

    if (canCoalesce) {
      // Keep the older "before" snapshot; just refresh the timestamp so a
      // continuing gesture keeps extending the same entry.
      previous.at = this.now();
      previous.label = result.label;
    } else {
      this.undoStack.push({ project, label: result.label, coalesceKey: result.coalesceKey, at: this.now() });
      if (this.undoStack.length > this.limit) this.undoStack.shift();
    }

    this.redoStack = [];
    return { project: result.project, changed: true, label: result.label };
  }

  /**
   * Records a state change that did not come from a command but should still be
   * undoable as one unit — used by import and recording, where the audio work
   * happens outside the command layer and the resulting project edit is applied
   * as a batch.
   */
  pushCheckpoint(before: Project, label: string): void {
    this.undoStack.push({ project: before, label, coalesceKey: null, at: this.now() });
    if (this.undoStack.length > this.limit) this.undoStack.shift();
    this.redoStack = [];
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undoLabel(): string | null {
    return this.undoStack[this.undoStack.length - 1]?.label ?? null;
  }

  redoLabel(): string | null {
    return this.redoStack[this.redoStack.length - 1]?.label ?? null;
  }

  undo(current: Project): Project | null {
    const entry = this.undoStack.pop();
    if (!entry) return null;
    this.redoStack.push({ project: current, label: entry.label, coalesceKey: null, at: this.now() });
    return entry.project;
  }

  redo(current: Project): Project | null {
    const entry = this.redoStack.pop();
    if (!entry) return null;
    this.undoStack.push({ project: current, label: entry.label, coalesceKey: null, at: this.now() });
    return entry.project;
  }

  /** Ends any in-flight coalescing, so the next command starts a new entry. */
  breakCoalescing(): void {
    const previous = this.undoStack[this.undoStack.length - 1];
    if (previous) previous.coalesceKey = null;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }

  get depth(): { undo: number; redo: number } {
    return { undo: this.undoStack.length, redo: this.redoStack.length };
  }
}
